import { SelectQueryBuilder, sql } from 'kysely'
import { Database } from '../database'
import { SoftwareTypes } from '@/client'

export enum ApplicationSourceFilter {
  MANUALLY = 'manually',
  INTEGRATION = 'integration',
  SSO = 'sso',
}

export type ApplicationFilter = {
  id?: string
  ids?: string[]
  displayName?: string
  updatedAfter?: string
  isDeleted?: boolean
  createdAfter?: string
  createdBefore?: string
  updatedBefore?: string
  deletedBefore?: string
  deletedAfter?: string
  tags?: string[]
  isSSOProvider?: boolean
  isSource?: ApplicationSourceFilter[]
  search?: string
  hideWithoutCosts?: boolean
  hideWithCosts?: boolean
  hideWithoutAccount?: boolean
  canDisableLicense?: boolean
  canEnableLicense?: boolean
  canDeleteUser?: boolean
}

export function applyApplicationFilters<T>(
  query: SelectQueryBuilder<Database, 'application', T>,
  filters: ApplicationFilter
): SelectQueryBuilder<Database, 'application', T> {
  if (filters.id) {
    query = query.where('_id', '=', filters.id)
  }
  if (filters.ids?.length) {
    query = query.where('_id', 'in', filters.ids)
  }
  if (filters.createdBefore) {
    query = query.where('created', '<', filters.createdBefore)
  }
  if (filters.createdAfter) {
    query = query.where('created', '>', filters.createdAfter)
  }
  if (filters.updatedBefore) {
    query = query.where('updated', '<', filters.updatedBefore)
  }
  if (filters.updatedAfter) {
    query = query.where('updated', '>', filters.updatedAfter)
  }
  if (filters.deletedBefore) {
    query = query.where('deleted_at', '<', filters.deletedBefore)
  }
  if (filters.deletedAfter) {
    query = query.where('deleted_at', '>', filters.deletedAfter)
  }
  if (filters.isDeleted !== true) {
    query = query.where('deleted', '=', filters.isDeleted ? 1 : 0)
  }
  if (filters.displayName) {
    query = query.where('display_name', 'like', `%${filters.displayName}%`)
  }
  if (filters.tags?.length) {
    query = query.where((eb) =>
      eb.and((filters.tags ?? []).map((tag) => eb('tags', 'like', `%${tag}%`)))
    )
  }

  if (filters.canEnableLicense) {
    query = query.where((eb) =>
      eb(
        sql`json_extract(${eb.ref('information')}, '$.implements_enable_licence_method')`,
        '=',
        true
      )
    )
  }
  if (filters.canDisableLicense) {
    query = query.where((eb) =>
      eb(
        sql`json_extract(${eb.ref('information')}, '$.implements_disable_licence_method')`,
        '=',
        true
      )
    )
  }
  if (filters.canDeleteUser) {
    query = query.where((eb) =>
      eb(
        sql`json_extract(${eb.ref('information')}, '$.implements_delete_user_method')`,
        '=',
        true
      )
    )
  }

  if (filters.isSource?.length) {
    query = query.where((eb) => {
      const conditions = []
      if (filters.isSource?.includes(ApplicationSourceFilter.MANUALLY)) {
        conditions.push(eb('type', '=', SoftwareTypes.MANUALLY))
      }

      if (filters.isSource?.includes(ApplicationSourceFilter.INTEGRATION)) {
        conditions.push(
          eb.and([
            eb('type', 'in', [
              SoftwareTypes.OAUTH,
              SoftwareTypes.BOT,
              SoftwareTypes.TOKEN,
            ]),
            eb(
              sql`json_extract(${eb.ref('information')}, '$.sso_provider')`,
              '=',
              false
            ),
          ])
        )
      }
      if (filters.isSource?.includes(ApplicationSourceFilter.SSO)) {
        // this filter is currently handled in the component itself until the subaccounts are refactored
      }

      return eb.or(conditions)
    })
  }
  if (filters.isSSOProvider !== undefined) {
    query = query.where('sso_provider', '=', filters.isSSOProvider ? 1 : 0)
  }
  if (filters.search) {
    query = query.where((eb) =>
      eb.or([
        eb('software_name', 'like', `%${filters.search}%`),
        eb('display_name', 'like', `%${filters.search}%`),
      ])
    )
  }
  if (filters.hideWithoutCosts) {
    query = query.where((eb) => eb.and([eb('application_costs', '>', 0)]))
  }
  if (filters.hideWithCosts) {
    query = query.where((eb) => eb.and([eb('application_costs', '=', 0)]))
  }
  if (filters.hideWithoutAccount) {
    query = query.where((eb) => eb.and([eb('user_count', '>', 0)]))
  }
  return query
}
